import { useState, useEffect, useCallback, useRef } from 'react'
import { getAddress, isAddress, type Address, type Hex } from 'viem'
import {
  AppShell,
  ConnectionSection,
  ControllerInfo,
  ControllersView,
  PermissionSelector,
  AllowedCallsEditor,
  AllowedDataKeysEditor,
  AuthorizeButton,
  ReviewConfirmModal,
  RemoveControllerModal,
  TemplateMarketplace,
  PublisherTemplateForm,
  SuccessConfirmation,
} from './components'
import { useWallet } from './hooks/useWallet'
import { useAuthorization } from './hooks/useAuthorization'
import { parseUrlParams, convertEntriesToAllowedCalls, convertAllowedCallsToEntries, convertAllowedDataKeysToEntries, decodeAllowedCalls, decodeAllowedDataKeys, encodeAllowedCalls, encodeAllowedDataKeys, applyAllowedCallsScopedInvariant, applyAllowedDataKeysScopedInvariant, resolveTemplateAllowedCallsHex, resolveTemplateAllowedDataKeysHex, mergeAllowedCallsDedupe, mergeAllowedDataKeysDedupe } from './utils'
import type { AllowedCall, AllowedCallEntry, DataKeyEntry } from './utils'
import { getSkillPagePath, PERMISSION_PRESETS, PERMISSIONS, PERMISSION_CONTROL } from './constants'
import type { AppView } from './components'
import { useTemplateRegistry, type TemplateMetadata } from './hooks/useTemplateRegistry'
import type { PublisherTemplateFormInitialValues } from './components/PublisherTemplateForm'
import { SkillPage } from './SkillPage'

function stripBasePath(pathname: string): string {
  const base = import.meta.env.BASE_URL
  if (!base || base === '/') return pathname || '/'
  const b = base.endsWith('/') ? base.slice(0, -1) : base
  if (pathname.startsWith(b)) {
    const rest = pathname.slice(b.length) || '/'
    return rest.startsWith('/') ? rest : `/${rest}`
  }
  return pathname || '/'
}

function pathToAppView(pathname: string): AppView {
  const p = stripBasePath(pathname).replace(/\/$/, '') || '/'
  if (p === '/skill') return 'skill'
  return 'hub'
}

function appViewToPath(v: AppView): string {
  if (v === 'skill') return getSkillPagePath()
  const base = import.meta.env.BASE_URL.endsWith('/') ? import.meta.env.BASE_URL : `${import.meta.env.BASE_URL}/`
  const root = base.replace(/\/{2,}/g, '/')
  return root === '//' ? '/' : root
}

function App() {
  // Parse URL parameters
  const urlParams = parseUrlParams()
  
  // Wallet state
  const wallet = useWallet()
  
  // Controller address state
  const [controllerAddress, setControllerAddress] = useState<Address | null>(
    urlParams.controllerAddress || null
  )
  const [controllerLabel, setControllerLabel] = useState('')
  
  // Selected permissions (nothing selected by default when adding controller)
  const [permissions, setPermissions] = useState<bigint>(
    urlParams.preset && PERMISSION_PRESETS[urlParams.preset]
      ? PERMISSION_PRESETS[urlParams.preset].permissions
      : 0n
  )
  const [permissionsSource, setPermissionsSource] = useState<'default' | 'existing' | 'user' | 'template'>('default')
  
  // Authorization state
  const authorization = useAuthorization(
    wallet.address,
    wallet.walletClient,
    wallet.publicClient
  )
  
  // AllowedCalls and AllowedDataKeys state
  const [allowedCallEntries, setAllowedCallEntries] = useState<AllowedCallEntry[]>([])
  const [allowedDataKeyEntries, setAllowedDataKeyEntries] = useState<DataKeyEntry[]>([])
  
  // Error state
  const [error, setError] = useState<string | null>(null)
  // Existing permissions for the controller (if any)
  const [existingPermissions, setExistingPermissions] = useState<bigint | null>(null)
  // Original allowed calls/data keys hex when editing (to detect real changes)
  const [originalAllowedCallsHex, setOriginalAllowedCallsHex] = useState<Hex | null>(null)
  const [originalAllowedDataKeysHex, setOriginalAllowedDataKeysHex] = useState<Hex | null>(null)
  // Track which controller we've already checked to avoid re-running
  const lastCheckedController = useRef<string | null>(null)

  const [view, setView] = useState<AppView>(() =>
    typeof window !== 'undefined' ? pathToAppView(window.location.pathname) : 'hub',
  )
  const [showAddControllerFlow, setShowAddControllerFlow] = useState(!!urlParams.controllerAddress)
  const [isReviewOpen, setIsReviewOpen] = useState(false)
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false)
  const [controllersRefreshToken, setControllersRefreshToken] = useState(0)
  const [actionInProgressFor, setActionInProgressFor] = useState<Address | null>(null)
  const [txControllerAddress, setTxControllerAddress] = useState<Address | null>(null)
  const [templateFormInitialValues, setTemplateFormInitialValues] = useState<PublisherTemplateFormInitialValues | null>(null)

  const registry = useTemplateRegistry({
    enabled: view === 'marketplace' || view === 'add-template',
  })

  useEffect(() => {
    const onPop = () => setView(pathToAppView(window.location.pathname))
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [])

  useEffect(() => {
    const next = appViewToPath(view)
    const cur = window.location.pathname.replace(/\/$/, '') || '/'
    const n = next.replace(/\/$/, '') || '/'
    if (cur !== n) {
      window.history.pushState({ view }, '', next)
    }
  }, [view])

  const scrollToAddControllerFlow = useCallback(() => {
    const run = () => {
      document.getElementById('add-controller-flow')?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      })
    }
    // Hub + add-controller section must paint after view/state updates; double rAF + delay lands below the fold reliably.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setTimeout(run, 220)
      })
    })
  }, [])

  // Check for existing controller when wallet connects or controller address changes
  useEffect(() => {
    const checkKey = `${wallet.address}-${controllerAddress}`
    if (!wallet.isConnected || !controllerAddress || lastCheckedController.current === checkKey) {
      if (!wallet.isConnected || !controllerAddress) {
        setExistingPermissions(null)
        lastCheckedController.current = null
      }
      return
    }
    lastCheckedController.current = checkKey

    const checkController = async () => {
      const existing = await authorization.checkExistingController(controllerAddress)
      if (existing.exists && existing.permissionsBigInt) {
        setExistingPermissions(existing.permissionsBigInt)
        if (permissionsSource === 'default' || permissionsSource === 'existing') {
          setPermissions(existing.permissionsBigInt)
          setPermissionsSource('existing')
        }
        // Template install pre-fills AllowedCalls / AllowedDataKeys from the registry; keep that preview
        // when the controller already exists on-chain (user is extending/updating toward the template).
        if (permissionsSource !== 'template') {
          if (existing.allowedCallsHex) {
            const calls = decodeAllowedCalls(existing.allowedCallsHex)
            setAllowedCallEntries(convertAllowedCallsToEntries(calls))
          } else {
            setAllowedCallEntries([])
          }
          if (existing.allowedDataKeysHex) {
            const keys = decodeAllowedDataKeys(existing.allowedDataKeysHex)
            setAllowedDataKeyEntries(convertAllowedDataKeysToEntries(keys))
          } else {
            setAllowedDataKeyEntries([])
          }
        }
        setOriginalAllowedCallsHex(existing.allowedCallsHex ?? null)
        setOriginalAllowedDataKeysHex(existing.allowedDataKeysHex ?? null)
        setError(null)
      } else {
        setExistingPermissions(null)
        // Template install pre-fills restrictions before the controller exists on-chain;
        // do not wipe that state when the lookup returns "not yet added".
        if (permissionsSource !== 'template') {
          setAllowedCallEntries([])
          setAllowedDataKeyEntries([])
        }
        setOriginalAllowedCallsHex(null)
        setOriginalAllowedDataKeysHex(null)
      }
    }
    checkController()
  }, [wallet.isConnected, wallet.address, controllerAddress, authorization, permissionsSource])

  // SUPER permission interaction logic
  const hasSuperCall = (permissions & BigInt(PERMISSIONS.SUPER_CALL)) !== 0n
  const hasSuperSetData = (permissions & BigInt(PERMISSIONS.SUPER_SETDATA)) !== 0n
  const hasCallRelated = (permissions & (
    BigInt(PERMISSIONS.CALL) | BigInt(PERMISSIONS.STATICCALL) |
    BigInt(PERMISSIONS.DELEGATECALL) | BigInt(PERMISSIONS.TRANSFERVALUE)
  )) !== 0n
  const hasSetData = (permissions & BigInt(PERMISSIONS.SETDATA)) !== 0n

  // When AllowedCalls entries change: auto-untick SUPER_CALL, ensure CALL is on
  const handleAllowedCallsChange = useCallback((entries: AllowedCallEntry[]) => {
    setAllowedCallEntries(entries)
    if (entries.length > 0) {
      setPermissions(prev => {
        // This ensures scoped permissions replace unrestricted super permissions.
        return applyAllowedCallsScopedInvariant(prev)
      })
    }
  }, [])

  // When AllowedDataKeys entries change: auto-untick SUPER_SETDATA, ensure SETDATA is on
  const handleAllowedDataKeysChange = useCallback((entries: DataKeyEntry[]) => {
    setAllowedDataKeyEntries(entries)
    if (entries.length > 0) {
      setPermissions(prev => {
        // This ensures scoped permissions replace unrestricted super permissions.
        return applyAllowedDataKeysScopedInvariant(prev)
      })
    }
  }, [])

  // Handle authorization (actual on-chain write)
  const performAuthorize = useCallback(async () => {
    if (!controllerAddress) {
      console.error('[App] Authorization attempted without controller address')
      setError('Please enter a controller address')
      return
    }

    // Pre-flight check: are permissions and restrictions identical to what's already on-chain?
    const currentCallsHex = allowedCallEntries.length > 0
      ? encodeAllowedCalls(convertEntriesToAllowedCalls(allowedCallEntries))
      : ('0x' as Hex)
    const currentDataKeysHex = allowedDataKeyEntries.length > 0
      ? encodeAllowedDataKeys(allowedDataKeyEntries.map((e) => e.key as Hex))
      : ('0x' as Hex)
    const originalCalls = originalAllowedCallsHex ?? ('0x' as Hex)
    const originalDataKeys = originalAllowedDataKeysHex ?? ('0x' as Hex)
    const hasNoChanges =
      existingPermissions !== null &&
      permissions === existingPermissions &&
      currentCallsHex === originalCalls &&
      currentDataKeysHex === originalDataKeys
    if (hasNoChanges) {
      console.warn('[App] No permission or restriction changes detected, skipping transaction')
      setError('NO_CHANGES_NEEDED')
      return
    }

    setError(null)
    setTxControllerAddress(controllerAddress)
    
    // Convert UI entries:
    // - `undefined` preserves on-chain restrictions
    // - `[]` explicitly clears restrictions (only when there was something to clear)
    const convertedCalls =
      allowedCallEntries.length > 0 ? convertEntriesToAllowedCalls(allowedCallEntries) : null
    const hadOriginalCalls = !!(originalAllowedCallsHex && originalAllowedCallsHex !== ('0x' as Hex))
    const hadOriginalDataKeys = !!(originalAllowedDataKeysHex && originalAllowedDataKeysHex !== ('0x' as Hex))

    const allowedCalls: AllowedCall[] | undefined =
      allowedCallEntries.length > 0
        ? convertedCalls!.length > 0
          ? convertedCalls!
          : []
        : existingPermissions !== null
          ? hadOriginalCalls
            ? []
            : undefined
          : undefined

    const allowedDataKeys: Hex[] | undefined =
      allowedDataKeyEntries.length > 0
        ? allowedDataKeyEntries.map((e) => e.key as Hex)
        : existingPermissions !== null
          ? hadOriginalDataKeys
            ? []
            : undefined
          : undefined
    
    await authorization.authorize({
      controllerAddress,
      permissions,
      allowedCalls,
      allowedDataKeys,
      controllerLabel: controllerLabel.trim() || undefined,
    })
    setControllersRefreshToken((t) => t + 1)
    wallet.refetchProfile()
  }, [controllerAddress, permissions, authorization, existingPermissions, originalAllowedCallsHex, originalAllowedDataKeysHex, allowedCallEntries, allowedDataKeyEntries, wallet, controllerLabel])

  // First open a review step (keeps on-chain behavior identical; just adds UX)
  const handleAuthorizeClick = useCallback(() => {
    if (!controllerAddress) {
      setError('Please enter a controller address')
      return
    }
    const currentCallsHex = allowedCallEntries.length > 0
      ? encodeAllowedCalls(convertEntriesToAllowedCalls(allowedCallEntries))
      : ('0x' as Hex)
    const currentDataKeysHex = allowedDataKeyEntries.length > 0
      ? encodeAllowedDataKeys(allowedDataKeyEntries.map((e) => e.key as Hex))
      : ('0x' as Hex)
    const originalCalls = originalAllowedCallsHex ?? ('0x' as Hex)
    const originalDataKeys = originalAllowedDataKeysHex ?? ('0x' as Hex)
    const hasNoChanges =
      existingPermissions !== null &&
      permissions === existingPermissions &&
      currentCallsHex === originalCalls &&
      currentDataKeysHex === originalDataKeys
    if (hasNoChanges) {
      setError('NO_CHANGES_NEEDED')
      return
    }
    setError(null)
    setIsReviewOpen(true)
  }, [controllerAddress, existingPermissions, permissions, originalAllowedCallsHex, originalAllowedDataKeysHex, allowedCallEntries, allowedDataKeyEntries])

  const handleConfirmAuthorize = useCallback(async () => {
    setIsReviewOpen(false)
    await performAuthorize()
  }, [performAuthorize])

  // Handle success modal close
  const handleSuccessClose = useCallback(() => {
    authorization.reset()
    wallet.refetchProfile()
    setControllersRefreshToken((t) => t + 1)
    setShowAddControllerFlow(false)
    setControllerAddress(null)
    setAllowedCallEntries([])
    setAllowedDataKeyEntries([])
    setExistingPermissions(null)
    setOriginalAllowedCallsHex(null)
    setOriginalAllowedDataKeysHex(null)
    setTxControllerAddress(null)
    setControllerLabel('')
  }, [authorization, wallet])

  const handleRemoveController = useCallback(async (targetController: Address) => {
    setError(null)
    setActionInProgressFor(targetController)
    setTxControllerAddress(targetController)
    try {
      await authorization.removeControllerStrict(targetController)
      setControllersRefreshToken((t) => t + 1)
      wallet.refetchProfile()
    } finally {
      setActionInProgressFor(null)
    }
  }, [authorization, wallet])

  const startEditController = useCallback((targetController: Address) => {
    setView('hub')
    setShowAddControllerFlow(true)
    setControllerAddress(targetController)
    setAllowedCallEntries([])
    setAllowedDataKeyEntries([])
    setExistingPermissions(null)
    setPermissionsSource('default')
    setControllerLabel('')
    setError(null)
    setTimeout(() => {
      document.getElementById('add-controller-flow')?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      })
    }, 100)
  }, [])

  const startAddController = useCallback(() => {
    setShowAddControllerFlow(true)
    setControllerAddress(null)
    setPermissions(0n)
    setPermissionsSource('default')
    setAllowedCallEntries([])
    setAllowedDataKeyEntries([])
    setExistingPermissions(null)
    setOriginalAllowedCallsHex(null)
    setOriginalAllowedDataKeysHex(null)
    setControllerLabel('')
    setError(null)
    setView('hub')
    scrollToAddControllerFlow()
  }, [scrollToAddControllerFlow])

  const startAddTemplate = useCallback((initialValues?: PublisherTemplateFormInitialValues | null) => {
    setTemplateFormInitialValues(initialValues ?? null)
    setView('add-template')
  }, [])

  const installTemplate = useCallback(
    async (
      t: { publisherUP: Address; permissions: bigint; allowedCalls: Hex; allowedERC725YDataKeys: Hex; chainId: bigint },
      meta: TemplateMetadata | null,
    ) => {
      // Registry is on LUKSO mainnet (42). For safety, warn via error state if mismatched network.
      if (wallet.chainId && wallet.chainId !== PERMISSION_CONTROL.chainId) {
        setError(`Wrong network. Switch to chainId ${PERMISSION_CONTROL.chainId} to install templates.`)
        setView('marketplace')
        return
      }

      const effectiveCallsHex = resolveTemplateAllowedCallsHex(t.allowedCalls, meta)
      const effectiveDataKeysHex = resolveTemplateAllowedDataKeysHex(t.allowedERC725YDataKeys, meta)

      let calls = decodeAllowedCalls(effectiveCallsHex)
      let dataKeys = decodeAllowedDataKeys(effectiveDataKeysHex)

      const raw = meta?.app?.controllerAddress?.trim()
      const controllerFromMeta = raw && isAddress(raw) ? getAddress(raw) : null
      const controllerAddr = (controllerFromMeta ?? t.publisherUP) as Address

      const existing = await authorization.checkExistingController(controllerAddr)

      if (existing.exists && existing.allowedCallsHex) {
        calls = mergeAllowedCallsDedupe(decodeAllowedCalls(existing.allowedCallsHex), calls)
      }
      if (existing.exists && existing.allowedDataKeysHex) {
        dataKeys = mergeAllowedDataKeysDedupe(decodeAllowedDataKeys(existing.allowedDataKeysHex), dataKeys)
      }

      const callEntries = convertAllowedCallsToEntries(calls)
      const dataKeyEntries = convertAllowedDataKeysToEntries(dataKeys)

      let newPerms = t.permissions
      if (existing.exists && existing.permissionsBigInt) {
        newPerms = existing.permissionsBigInt | t.permissions
      }
      if (callEntries.length > 0) {
        newPerms = applyAllowedCallsScopedInvariant(newPerms)
      }
      if (dataKeyEntries.length > 0) {
        newPerms = applyAllowedDataKeysScopedInvariant(newPerms)
      }

      // Force existing-controller lookup to run so existingPermissions / original* hex reflect chain,
      // even if this address was already checked earlier in the session (see lastCheckedController gate).
      lastCheckedController.current = null

      setView('hub')
      setShowAddControllerFlow(true)
      setControllerAddress(controllerAddr)
      const label = meta?.app?.name?.trim() || meta?.name?.trim() || ''
      setControllerLabel(label)
      setPermissions(newPerms)
      setPermissionsSource('template')
      setAllowedCallEntries(callEntries)
      setAllowedDataKeyEntries(dataKeyEntries)
      setExistingPermissions(null)
      setOriginalAllowedCallsHex(null)
      setOriginalAllowedDataKeysHex(null)
      setError(null)
      scrollToAddControllerFlow()
    },
    [wallet.chainId, scrollToAddControllerFlow, authorization],
  )

  // Is ready to authorize?
  const isReady = wallet.isConnected &&
    wallet.isWalletClientReady &&
    controllerAddress !== null &&
    /^0x[a-fA-F0-9]{40}$/.test(controllerAddress) &&
    permissions > 0n

  return (
    <AppShell
      view={view}
      onViewChange={setView}
      wallet={{
        isConnected: wallet.isConnected,
        isConnecting: wallet.isConnecting,
        address: wallet.address,
        chainId: wallet.chainId,
        profileData: wallet.profileData,
        connectionMethod: wallet.connectionMethod,
        isExtensionAvailable: wallet.isExtensionAvailable,
        isWalletConnectAvailable: wallet.isWalletConnectAvailable,
        connectExtension: wallet.connectExtension,
        connectWalletConnect: wallet.connectWalletConnect,
        disconnect: wallet.disconnect,
      }}
    >
      {view === 'skill' && <SkillPage onBack={() => setView('hub')} />}

      {view === 'hub' && (
        <div className="space-y-6">
          <section>
            <ConnectionSection
              isConnected={wallet.isConnected}
              isConnecting={wallet.isConnecting}
              address={wallet.address}
              profileData={wallet.profileData}
              isExtensionAvailable={wallet.isExtensionAvailable}
              isWalletConnectAvailable={wallet.isWalletConnectAvailable}
              error={wallet.error}
              onConnectExtension={wallet.connectExtension}
              onConnectWalletConnect={wallet.connectWalletConnect}
              onDisconnect={wallet.disconnect}
            />
          </section>

          {wallet.isConnected && wallet.address && wallet.publicClient && (
            <section>
              <ControllersView
                upAddress={wallet.address}
                publicClient={wallet.publicClient}
                refreshToken={controllersRefreshToken}
                onStartAddController={startAddController}
                onEditController={startEditController}
                actionInProgressFor={actionInProgressFor}
              />
            </section>
          )}

          {wallet.isConnected && showAddControllerFlow && (
            <section id="add-controller-flow" className="pt-16 space-y-4 scroll-mt-10 md:scroll-mt-12">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h3 className="text-lg font-semibold">
                    {existingPermissions != null ? 'Edit controller' : 'Add controller'}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    Choose a controller, set what it can do, then confirm onchain.
                  </p>
                </div>
                <button
                  type="button"
                  className="btn-secondary text-sm"
                  onClick={() => setShowAddControllerFlow(false)}
                >
                  Close
                </button>
                </div>

              <ControllerInfo
                controllerAddress={controllerAddress}
                onAddressChange={setControllerAddress}
                controllerLabel={controllerLabel}
                onControllerLabelChange={setControllerLabel}
              />

              {controllerAddress && (
                <>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Set permissions</h3>
                  </div>
                  <PermissionSelector
                    value={permissions}
                    onChange={(p) => { setPermissions(p); setPermissionsSource('user') }}
                    initialPreset={urlParams.preset}
                    existingPermissions={existingPermissions}
                    allowedCallsCount={allowedCallEntries.length}
                    allowedDataKeysCount={allowedDataKeyEntries.length}
                  />
                </>
              )}
            </section>
          )}

          {/* Allowed Calls — show when any call-related permission is selected; super alert lives inside the card */}
          {wallet.isConnected && showAddControllerFlow && controllerAddress && permissions > 0n && (hasCallRelated || hasSuperCall) && (
            <section>
              <AllowedCallsEditor
                entries={allowedCallEntries}
                onChange={handleAllowedCallsChange}
                hasSuperCall={hasSuperCall}
                chainId={wallet.chainId ?? 42}
              />
            </section>
          )}

          {/* Allowed Data Keys — show when setData or super setData is selected; super alert lives inside the card */}
          {wallet.isConnected && showAddControllerFlow && controllerAddress && permissions > 0n && (hasSetData || hasSuperSetData) && (
            <section>
              <AllowedDataKeysEditor
                entries={allowedDataKeyEntries}
                onChange={handleAllowedDataKeysChange}
                hasSuperSetData={hasSuperSetData}
              />
            </section>
          )}

          {/* No loud warnings here — keep UI calm. Review modal summarizes before signing. */}

          {/* Error display */}
          {(error || authorization.error) && (
            <ErrorDisplay rawError={error || authorization.error || ''} />
          )}

          {/* Authorize and Remove controller — stacked: authorize on top, remove below; full width */}
          {wallet.isConnected && showAddControllerFlow && (
            <section className="flex flex-col items-stretch gap-3 w-full">
              <AuthorizeButton
                status={authorization.status}
                permissions={permissions}
                isReady={isReady && !error}
                onAuthorize={handleAuthorizeClick}
                isWalletConnected={wallet.isConnected}
                isEditing={existingPermissions != null}
              />
              {controllerAddress && existingPermissions != null && (
                <button
                  type="button"
                  className="btn-danger-outline text-sm w-full"
                  onClick={() => setIsRemoveModalOpen(true)}
                >
                  Remove controller
                </button>
              )}
            </section>
          )}

        </div>
      )}

      {view === 'marketplace' && (
        <>
          <TemplateMarketplace
            templates={registry.templates}
            getMetadataForTemplate={registry.getMetadataForTemplate}
            status={registry.status}
            error={registry.error}
            isConnected={wallet.isConnected}
            onInstall={(t, meta) => installTemplate(t, meta)}
            onAddTemplate={() => startAddTemplate(null)}
            onCreateNewVersion={(initialValues) => startAddTemplate(initialValues)}
            isConnecting={wallet.isConnecting}
            isExtensionAvailable={wallet.isExtensionAvailable}
            isWalletConnectAvailable={wallet.isWalletConnectAvailable}
            connectError={wallet.error}
            onConnectExtension={wallet.connectExtension}
            onConnectWalletConnect={wallet.connectWalletConnect}
            publicClient={wallet.publicClient}
            upAddress={wallet.address}
            walletClient={wallet.walletClient}
            onTemplateActionSuccess={() => registry.refresh()}
          />

        </>
      )}

      {view === 'add-template' && (
        <div className="flex flex-col flex-1 min-h-0 overflow-auto">
          <div className="flex items-center gap-3 px-4 py-3 border-b border-white/10 flex-shrink-0">
            <button
              type="button"
              onClick={() => setView('marketplace')}
              className="flex items-center gap-2 p-2 -ml-2 rounded-xl text-gray-400 hover:text-white hover:bg-white/10 transition-colors"
              aria-label="Back to Templates"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
              <span className="text-sm font-medium">Templates</span>
            </button>
          </div>
          <div className="p-4 flex-1 min-h-0">
            <h2 className="text-xl font-semibold text-gray-100 mb-4">New project template</h2>
            <PublisherTemplateForm
              isConnected={wallet.isConnected}
              upAddress={wallet.address}
              chainId={wallet.chainId}
              walletClient={wallet.walletClient}
              publicClient={wallet.publicClient}
              profileData={wallet.profileData}
              initialValues={templateFormInitialValues}
              onPublished={() => {
                registry.refresh()
                setTemplateFormInitialValues(null)
                setView('marketplace')
              }}
              onClose={() => {
                setTemplateFormInitialValues(null)
                setView('marketplace')
              }}
            />
          </div>
        </div>
      )}

      {wallet.isConnected && wallet.address && controllerAddress && (
        <ReviewConfirmModal
          isOpen={isReviewOpen}
          onClose={() => setIsReviewOpen(false)}
          onConfirm={handleConfirmAuthorize}
          upAddress={wallet.address}
          controllerAddress={controllerAddress}
          permissions={permissions}
          allowedCallEntries={allowedCallEntries}
          allowedDataKeyEntries={allowedDataKeyEntries}
          isConfirmDisabled={authorization.status === 'pending' || authorization.status === 'preparing'}
        />
      )}

      {controllerAddress && (
        <RemoveControllerModal
          isOpen={isRemoveModalOpen}
          onClose={() => setIsRemoveModalOpen(false)}
          onConfirm={() => {
            void handleRemoveController(controllerAddress)
            setIsRemoveModalOpen(false)
          }}
          controllerAddress={controllerAddress}
          isConfirmDisabled={authorization.status === 'pending' || authorization.status === 'preparing'}
        />
      )}

      {/* Success Modal */}
      {authorization.status === 'success' && authorization.txHash && txControllerAddress && wallet.address && (
        <SuccessConfirmation
          txHash={authorization.txHash}
          controllerAddress={txControllerAddress}
          chainId={wallet.chainId || 42}
          onClose={handleSuccessClose}
        />
      )}
    </AppShell>
  )
}

// Friendly error messages for common wallet/contract errors
function getFriendlyError(raw: string): { friendly: string; isWarning: boolean } | null {
  if (raw === 'NO_CHANGES_NEEDED' || raw.includes('already set in an identical way') || raw.includes('identical')) {
    return { friendly: 'No changes needed — these permissions are already set for this controller.', isWarning: true }
  }
  if (raw.includes('User rejected') || raw.includes('user rejected') || raw.includes('User denied')) {
    return { friendly: 'Transaction was cancelled. You can try again when ready.', isWarning: true }
  }
  if (raw.includes('insufficient funds') || raw.includes('Insufficient funds')) {
    return { friendly: 'Your account doesn\'t have enough LYX to pay for gas fees.', isWarning: false }
  }
  if (raw.includes('not the owner')) {
    return { friendly: 'You are not the owner of this Universal Profile. Only the owner can manage controller permissions.', isWarning: false }
  }
  return null
}

function ErrorDisplay({ rawError }: { rawError: string }) {
  const [showDetails, setShowDetails] = useState(false)
  const parsed = getFriendlyError(rawError)
  const isWarning = parsed?.isWarning ?? false

  const bgClass = isWarning
    ? 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800'
    : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
  const iconClass = isWarning ? 'text-yellow-500' : 'text-red-500'
  const titleClass = isWarning ? 'text-yellow-700 dark:text-yellow-400' : 'text-red-700 dark:text-red-400'
  const textClass = isWarning ? 'text-yellow-600 dark:text-yellow-300' : 'text-red-600 dark:text-red-300'

  return (
    <div className={`p-4 rounded-lg border ${bgClass}`}>
      <div className="flex items-start gap-3">
        <svg className={`w-5 h-5 flex-shrink-0 mt-0.5 ${iconClass}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <div className="min-w-0 flex-1">
          <h4 className={`font-medium ${titleClass}`}>
            {isWarning ? 'Warning' : 'Error'}
          </h4>
          <p className={`text-sm mt-1 ${textClass}`}>
            {parsed ? parsed.friendly : rawError}
          </p>
          {parsed && rawError !== 'NO_CHANGES_NEEDED' && (
            <button
              onClick={() => setShowDetails(!showDetails)}
              className={`text-xs mt-2 underline opacity-70 hover:opacity-100 ${textClass}`}
            >
              {showDetails ? 'Hide technical details' : 'Show technical details'}
            </button>
          )}
          {showDetails && (
            <pre className={`text-xs mt-2 p-2 rounded bg-black/5 dark:bg-white/5 whitespace-pre-wrap break-words overflow-wrap-anywhere ${textClass}`}>
              {rawError}
            </pre>
          )}
        </div>
      </div>
    </div>
  )
}

export default App
